import { ObjectId } from '@freightview/object-id'
import { isEqual, omit } from 'lodash-es'

import type {
  LoadItem,
  LoadTemplateDTO,
  TemplateEquipment,
  TemplateHandlingUnit,
  TemplateLocation,
  UpsertLoadTemplateDTO,
  Workflow,
} from '@fv/models'
import { type DTO, removeNilOrEmpty, removeUndef } from '@fv/models/core'

import { parseShareEmails } from '@/utils/shares'

import { billToMapper, defaultFormBillTo } from '../../billTo/billToFuncs'
import {
  buildHandlingUnitDTO,
  defaultLoadHandlingUnit,
  toLoadHandlingUnitFormModel,
} from '../../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../../commodities/load-items/types'
import type { OptionalShipmentEquipmentFormModel } from '../../shipment-equipment-form/types'
import {
  buildLoadLocationDto,
  defaultDestination,
  defaultOrigin,
  type ShipmentLocationFormModel,
} from '../../shipment-location-form/types'
import type { ShipmentTemplateFormModel } from './types'

export const templateLocationToFormModel = (
  stored?: TemplateLocation,
  type: 'origin' | 'destination' = 'origin',
): ShipmentLocationFormModel => {
  const defaults = type === 'origin' ? defaultOrigin() : defaultDestination()

  if (!stored) return defaults

  return {
    ...defaults,
    ...stored,
    company: stored.company ?? '',
    address: stored.address ?? '',
    address2: stored.address2 ?? '',
    city: stored.city ?? '',
    state: stored.state ?? '',
    postalCode: stored.postalCode ?? '',
    country: stored.country ?? 'us',
    timezone: stored.timezone ?? '',
    contactName: stored.contactName ?? '',
    contactEmail: stored.contactEmail ?? '',
    contactPhone: stored.contactPhone ?? '',
    contactPhoneExt: stored.contactPhoneExt ?? '',
    instructions: stored.instructions ?? '',
    shipType: stored.shipType ?? defaults.shipType,
    accessorials: stored.accessorials ?? [],
    opensAt: stored.opensAt as ShipmentLocationFormModel['opensAt'],
    closesAt: stored.closesAt as ShipmentLocationFormModel['closesAt'],
    copyBOL: !!stored.shares?.length,
    shares: stored.shares?.join(', ') ?? '',
    type: defaults.type,
    sequence: defaults.sequence,
    stopType: defaults.stopType,
  }
}

const buildTemplateLocation = (
  model: ShipmentLocationFormModel,
): TemplateLocation => {
  const dto = buildLoadLocationDto(model)
  const shares = model.copyBOL ? parseShareEmails(model.shares) : []

  return removeUndef({
    addressBookId: dto.addressBookId,
    accountLocationId: dto.accountLocationId,
    sequence: dto.sequence,
    shipType: dto.shipType,
    stopType: dto.stopType,
    company: dto.company || undefined,
    address: dto.address || undefined,
    address2: dto.address2 || undefined,
    adminArea3: dto.adminArea3,
    city: dto.city || undefined,
    state: dto.state || undefined,
    postalCode: dto.postalCode || undefined,
    country: dto.country || undefined,
    timezone: dto.timezone || undefined,
    stopDateType: dto.stopDateType,
    opensAt: dto.opensAt,
    closesAt: dto.closesAt,
    instructions: dto.instructions || undefined,
    accessorials: dto.accessorials,
    contactName: dto.contactName || undefined,
    contactEmail: dto.contactEmail,
    contactPhone: dto.contactPhone,
    contactPhoneExt: dto.contactPhoneExt,
    type: dto.type,
    flatbedHandlingType: dto.flatbedHandlingType,
    shares: shares.length ? shares : undefined,
  })
}

export const formLocationToTemplatePayload = (
  model: ShipmentLocationFormModel,
  defaults: ShipmentLocationFormModel,
): TemplateLocation | undefined => {
  const payload = buildTemplateLocation(model)
  return isEqual(payload, buildTemplateLocation(defaults)) ? undefined : payload
}

export const emptyTemplateEquipment =
  (): OptionalShipmentEquipmentFormModel => ({
    type: '',
    accessorials: [],
    alternateTypes: [],
  })

export const templateEquipmentToFormModel = (
  stored?: TemplateEquipment,
): OptionalShipmentEquipmentFormModel => ({
  ...emptyTemplateEquipment(),
  ...stored,
  type: stored?.type ?? '',
  accessorials: stored?.accessorials ?? [],
  alternateTypes: stored?.alternateTypes ?? [],
})

export const formEquipmentToTemplatePayload = (
  model: OptionalShipmentEquipmentFormModel,
): TemplateEquipment | undefined =>
  model.type
    ? removeUndef({
        type: model.type,
        alternateTypes: model.alternateTypes,
        accessorials: model.accessorials,
        description: model.description,
        declaredValue: model.declaredValue,
        weight: model.weight,
      })
    : undefined

const withId = <T extends { _id?: string }>(item: T) => ({
  ...item,
  _id: item._id ?? new ObjectId().toHexString(),
})

export const templateItemToLoadItem = (
  item: DTO<TemplateHandlingUnit>,
  dropLocationSequence = 1,
): DTO<LoadItem> =>
  // Template items are partial by design; the form mappers tolerate missing fields
  ({
    ...withId(item),
    contains: item.contains?.map(withId),
    pickupLocationSequence: 0,
    dropLocationSequence,
  }) as DTO<LoadItem>

export const templateItemsToFormModel = (
  stored: DTO<TemplateHandlingUnit>[] | undefined,
  workflow: Workflow,
): LoadHandlingUnitFormModel[] =>
  stored?.length
    ? stored.map(item =>
        toLoadHandlingUnitFormModel(templateItemToLoadItem(item)),
      )
    : [defaultLoadHandlingUnit(workflow)]

const handlingUnitHasContent = (unit: LoadHandlingUnitFormModel) =>
  !!(
    unit.description?.trim() ||
    unit.quantity ||
    unit.weight ||
    unit.length ||
    unit.width ||
    unit.height ||
    unit.freightClass ||
    unit.fullNmfc ||
    unit.declaredValue ||
    unit.isHazardous ||
    unit.contains?.length
  )

export const formItemsToTemplatePayload = (
  items: LoadHandlingUnitFormModel[],
  workflow: Workflow,
): TemplateHandlingUnit[] | undefined => {
  const units = items.filter(handlingUnitHasContent).map(item => {
    const dto = buildHandlingUnitDTO(workflow)(item)
    return removeUndef({
      ...omit(dto, '_id', 'pickupLocationSequence', 'dropLocationSequence'),
      contains: dto.contains?.map(c => omit(c, '_id')),
      description: dto.description || undefined,
      quantity: workflow === 'parcel' ? dto.quantity : item.quantity,
      weight: dto.weight || undefined,
      weightUOM: dto.weight ? dto.weightUOM : undefined,
    }) as TemplateHandlingUnit
  })

  return units.length ? units : undefined
}

export const templateToFormModel = (
  template: LoadTemplateDTO,
): ShipmentTemplateFormModel => ({
  name: template.name,
  workflow: template.workflow,
  origin: templateLocationToFormModel(template.origin, 'origin'),
  destination: templateLocationToFormModel(template.destination, 'destination'),
  brandId: template.brandId,
  billTo: billToMapper.dtoToForm(template.billTo),
  equipment: templateEquipmentToFormModel(template.equipment),
  items: templateItemsToFormModel(template.items, template.workflow),
  glCode: template.glCode ?? '',
  tags: template.tags ?? [],
})

export const formModelToPayload = (
  model: ShipmentTemplateFormModel,
): UpsertLoadTemplateDTO => {
  const billTo = removeNilOrEmpty(billToMapper.loadToDTO(model.billTo))
  const hasBillTo = billTo && Object.keys(billTo).length > 1 // country is always present
  const glCode = model.glCode?.trim()
  const origin = formLocationToTemplatePayload(model.origin, defaultOrigin())
  const destination = formLocationToTemplatePayload(
    model.destination,
    defaultDestination(),
  )
  const items = formItemsToTemplatePayload(model.items, model.workflow)
  const equipment =
    model.workflow === 'truckload'
      ? formEquipmentToTemplatePayload(model.equipment)
      : undefined

  return {
    name: model.name.trim(),
    workflow: model.workflow,
    brandId: model.brandId,
    ...(origin && { origin }),
    ...(destination && { destination }),
    ...(hasBillTo && { billTo }),
    ...(equipment && { equipment }),
    ...(items && { items }),
    ...(glCode && { glCode }),
    ...(model.tags.length > 0 && { tags: model.tags }),
  }
}

export const emptyTemplateFormModel = (
  workflow: Workflow = 'ltl',
): ShipmentTemplateFormModel => ({
  name: '',
  workflow,
  origin: defaultOrigin(),
  destination: defaultDestination(),
  brandId: undefined,
  billTo: { ...defaultFormBillTo },
  equipment: emptyTemplateEquipment(),
  items: [defaultLoadHandlingUnit(workflow)],
  glCode: '',
  tags: [],
})
